import {useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FieldLabel from 'components/Form/Field/FieldLabel';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import InputTextDropdown from 'components/Form/InputTextWithDropdown';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import LinkButton from 'components/LinkButton';
import SectionHeader from 'components/SectionHeader';
import {H2} from 'components/Typography';
import useBeforeUnload from 'hooks/useBeforeUnload';
import lodash from 'lodash';
import {useBackend} from 'state/backend';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import isEmail from 'validator/lib/isEmail';
import {useChangeClientInbox} from './services';
import {labelWidth} from './styles.module.css';
import {ClientInboxEditBackend, ClientInboxEditFormFields} from './types';

type ClientInboxEditFormData = FormFields<ClientInboxEditFormFields>;

export type UseChangeClientInboxProps = {
    clientName: string;
    formData: ClientInboxEditFormData;
};

const ClientInboxEdit = () => {
    const {
        checkFlag,
        data: {
            allowClientinboxUsersToEditUsers,
            clientMid,
            clientName,
            contacts,
            recipient1,
            recipient2,
            recipient3,
        },
    } = useBackend<ClientInboxEditBackend>();

    const initialValues = {
        allowClientinboxUsersToEditUsers,
        recipient1,
        recipient2,
        recipient3,
    };

    const methods = useForm<ClientInboxEditFormData>({
        defaultValues: initialValues,
    });
    const {getValues, handleSubmit, trigger, watch} = methods;

    const [hasRecipient1BeenValidated, setHasRecipient1BeenValidated] =
        useState<boolean>(false);
    const [hasRecipient2BeenValidated, setHasRecipient2BeenValidated] =
        useState<boolean>(false);
    const [hasRecipient3BeenValidated, setHasRecipient3BeenValidated] =
        useState<boolean>(false);

    const {t} = useTranslation('Clients.Client.SendingSettings');

    const options = useMemo(() => {
        if (!contacts) {
            return [];
        }

        return contacts
            .sort((a, b) =>
                a.formattedName
                    .trim()
                    .localeCompare(b.formattedName.trim(), 'ja'),
            )
            .map((option) => ({
                label: `${option.value} (${option.formattedName})`,
                value: option.value,
            }));
    }, [contacts]);

    // Run validation on other fields with trigger() to check for duplicate email addresses
    useEffect(() => {
        // All email fields have been validated. Reset states to prevent infinite loop
        if (
            hasRecipient1BeenValidated &&
            hasRecipient2BeenValidated &&
            hasRecipient3BeenValidated
        ) {
            setHasRecipient1BeenValidated(false);
            setHasRecipient2BeenValidated(false);
            setHasRecipient3BeenValidated(false);

            return;
        }

        if (
            !hasRecipient1BeenValidated &&
            !hasRecipient2BeenValidated &&
            !hasRecipient3BeenValidated
        ) {
            return;
        }

        if (!hasRecipient1BeenValidated) {
            trigger('recipient1');
        }

        if (!hasRecipient2BeenValidated) {
            trigger('recipient2');
        }

        if (!hasRecipient3BeenValidated) {
            trigger('recipient3');
        }
    }, [
        hasRecipient1BeenValidated,
        hasRecipient2BeenValidated,
        hasRecipient3BeenValidated,
        trigger,
    ]);

    const {mutateAsync} = useChangeClientInbox();

    const {allowPageChange} = useBeforeUnload(
        !lodash.isEqual(watch(), initialValues),
    );

    const onSubmit = (formData: ClientInboxEditFormFields) => {
        mutateAsync({clientMid, formData})
            .then(() => {
                allowPageChange();
                setUrl(`/home/client/${clientMid}/securesend/`);
            })
            .catch(onApiFormError(methods));
    };

    const emailValidation = (value: string) => {
        if (value) {
            return isEmail(value) || t('form:error.emailInvalid');
        }

        return true;
    };

    const tooltipProps = {
        href: 'https://help.makeleaps.jp/ja/articles/8687198-makeleapsから書類を送付する方法#h_bc10350fef',
        message: t('clientInboxUsersTooltip'),
    };

    const fieldLabel = (
        <FieldLabel isSideBySide={true} tooltip={tooltipProps}>
            {t('clientInboxUsers')}
        </FieldLabel>
    );

    const allowEditUsersFieldLabel = (
        <FieldLabel isSideBySide={true}>
            <Trans
                components={[<br key={0} />]}
                i18nKey="allowClientInboxUsersToEditUsers"
                ns="Clients.Client.SendingSettings"
            />
        </FieldLabel>
    );

    return (
        <FormProvider {...methods}>
            <header className="flex flex-col">
                <div className="flex items-start justify-between">
                    <H2>{clientName}</H2>
                </div>
            </header>
            <SectionHeader
                subtitle={t('sectionHeaderSubTitle')}
                title={t('sectionHeaderTitle')}
            />
            <div className="w-[612px] ml-16">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <SideBySide
                        className={clsx('mt-5', labelWidth)}
                        spacing="sm"
                    >
                        {/* HACK: Replaces the word "email" in the aria-label to prevent browser from using email suggestions */}
                        <InputTextDropdown
                            aria-label="recipient1"
                            autoComplete="off"
                            buttonTooltipMessage={t('contactsAvailable')}
                            label={fieldLabel}
                            name="recipient1"
                            noOptionsButtonTooltipMessage={t('noContact')}
                            options={options}
                            rules={{
                                validate: (value: string) => {
                                    setHasRecipient1BeenValidated(true);

                                    const emailValidationResult =
                                        emailValidation(value);

                                    if (emailValidationResult !== true) {
                                        return emailValidationResult;
                                    }

                                    if (
                                        value &&
                                        [
                                            getValues('recipient2'),
                                            getValues('recipient3'),
                                        ].includes(value)
                                    ) {
                                        return t('duplicateEmails');
                                    }

                                    return undefined;
                                },
                            }}
                        />
                        <InputTextDropdown
                            autoComplete="off"
                            buttonTooltipMessage={t('contactsAvailable')}
                            name="recipient2"
                            noOptionsButtonTooltipMessage={t('noContact')}
                            options={options}
                            rules={{
                                validate: (value: string) => {
                                    setHasRecipient2BeenValidated(true);

                                    const emailValidationResult =
                                        emailValidation(value);

                                    if (emailValidationResult !== true) {
                                        return emailValidationResult;
                                    }

                                    if (
                                        value &&
                                        [
                                            getValues('recipient1'),
                                            getValues('recipient3'),
                                        ].includes(value)
                                    ) {
                                        return t('duplicateEmails');
                                    }

                                    return undefined;
                                },
                            }}
                        />
                        <InputTextDropdown
                            autoComplete="off"
                            buttonTooltipMessage={t('contactsAvailable')}
                            name="recipient3"
                            noOptionsButtonTooltipMessage={t('noContact')}
                            options={options}
                            rules={{
                                validate: (value: string) => {
                                    setHasRecipient3BeenValidated(true);
                                    const emailValidationResult =
                                        emailValidation(value);

                                    if (emailValidationResult !== true) {
                                        return emailValidationResult;
                                    }

                                    if (
                                        value &&
                                        [
                                            getValues('recipient1'),
                                            getValues('recipient2'),
                                        ].includes(value)
                                    ) {
                                        return t('duplicateEmails');
                                    }

                                    return undefined;
                                },
                            }}
                        />
                        {checkFlag(
                            'portal_allow_clientinbox_users_to_edit_users',
                        ) ? (
                            <Field
                                label={allowEditUsersFieldLabel}
                                name="allowClientinboxUsersToEditUsersLabel"
                                type="checkbox"
                            >
                                <Checkbox name="allowClientinboxUsersToEditUsers" />
                            </Field>
                        ) : null}
                        <FormResult />
                        <FormActions align="left" className="mt-4" spacing="sm">
                            <LinkButton
                                aria-label="cancel"
                                href={window.Urls.client_securesend_view(
                                    clientMid,
                                )}
                                kind="secondary"
                            >
                                {t('form:cancel')}
                            </LinkButton>
                            <SubmitButton>{t('saveSettings')}</SubmitButton>
                        </FormActions>
                    </SideBySide>
                </form>
            </div>
        </FormProvider>
    );
};

export default ClientInboxEdit;
